import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks } from 'date-fns';
import { es } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import { fetchEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';
import {
  getScheduleTypes,
  createScheduleType,
  updateScheduleType,
  deleteScheduleType,
  getShiftRoles,
  createShiftRole,
  updateShiftRole,
  deleteShiftRole,
  getWeeklyAssignments,
  createAssignments,
  updateAssignment,
  deleteAssignment,
} from '../services/scheduleManagement.services';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { ChevronLeft, ChevronRight, Plus, Trash2, Edit2, Clock, Users, Shield, CalendarDays, X, AlertTriangle, Eye, Table2, Timer } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { obtenerResumenSemana } from '@/pages/horasAutorizadas/services/horasAutorizadas.services';

const DAYS_OF_WEEK = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const DAYS_LABELS = { lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo' };

// Jornada ordinaria semanal: 42 h desde el 15/07/2026 (Ley 2101 de 2021; antes 44 h)
const BASE_WEEKLY_HOURS = 42;
const MAX_OVERTIME_HOURS = 12;
const MAX_WEEKLY_HOURS = BASE_WEEKLY_HOURS + MAX_OVERTIME_HOURS; // 54

// Trabajo nocturno: de 19:00 a 06:00 (la misma ventana que usa la liquidación de nómina)
const NIGHT_START = '19:00';
const NIGHT_END = '06:00';

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Inicio y fin de un rango en minutos; si el fin es antes del inicio, el turno pasa la medianoche
const rangeBounds = (range) => {
  const ini = toMinutes(range.inicio);
  let fin = toMinutes(range.fin);
  if (fin < ini) fin += 24 * 60;
  return [ini, fin];
};

// Ventanas nocturnas que puede tocar un turno de hasta dos días calendario
const NIGHT_WINDOWS = (() => {
  const s = toMinutes(NIGHT_START), e = toMinutes(NIGHT_END);
  return [[0, e], [s, 24 * 60 + e], [24 * 60 + s, 48 * 60]];
})();

const nightMinutes = (ini, fin) =>
  NIGHT_WINDOWS.reduce((acc, [ws, we]) => acc + Math.max(0, Math.min(fin, we) - Math.max(ini, ws)), 0);

// Minutos trabajados y nocturnos de un día. El almuerzo se descuenta del total; como no se
// sabe a qué hora se toma, las nocturnas solo se limitan a no superar lo trabajado.
const dayMinutes = (ranges = [], lunchMin = 0) => {
  let worked = 0, night = 0;
  for (const r of ranges) {
    const [ini, fin] = rangeBounds(r);
    worked += fin - ini;
    night += nightMinutes(ini, fin);
  }
  if (ranges.length > 0) worked -= Number(lunchMin) || 0;
  worked = Math.max(0, worked);
  return { worked, night: Math.min(night, worked) };
};

const round1 = (n) => Math.round(n * 10) / 10;

// Horas de la semana de una asignación o tipo de horario (sin almuerzo), mismas reglas que el
// backend de horas autorizadas
const weekHoursOf = (horarios, almuerzo) =>
  round1(DAYS_OF_WEEK.reduce((acc, d) => acc + dayMinutes(horarios?.[d] || [], almuerzo?.[d]).worked, 0) / 60);

const isDriverRol = (rol) => /conductor/i.test(String(rol || ''));

// Banda con las horas autorizadas del centro para la semana y cuánto se ha usado
const AuthorizedHoursBanner = ({ resumen, loading }) => {
  if (loading && !resumen) return null;
  if (!resumen) return null;
  if (resumen.limite == null) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
        <Timer className="h-4 w-4 shrink-0" />
        Este centro no tiene horas autorizadas configuradas: la planificación no tiene límite. Planificadas esta semana: <strong>{resumen.planificadas} h</strong>
      </div>
    );
  }
  const pct = resumen.limite > 0 ? (resumen.planificadas / resumen.limite) * 100 : 100;
  const exceeds = resumen.planificadas > resumen.limite;
  const color = exceeds ? 'bg-red-500' : pct >= 90 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className={`rounded-md border px-3 py-2 ${exceeds ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : 'bg-muted/30'}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold"><Timer className="h-4 w-4" /> Horas autorizadas: {resumen.limite} h</span>
        <Badge variant={resumen.fuente === 'semana' ? 'default' : 'secondary'} className="text-[10px]">
          {resumen.fuente === 'semana' ? 'Ajuste de esta semana' : 'Límite estándar'}
        </Badge>
        <span>Planificadas: <strong>{resumen.planificadas} h</strong></span>
        <span className={exceeds ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
          {exceeds ? `Excede ${round1(resumen.planificadas - resumen.limite)} h` : `Disponibles: ${resumen.disponibles} h`}
        </span>
        {resumen.horasConductores > 0 && <span className="text-xs text-muted-foreground">Conductores (no cuentan): {resumen.horasConductores} h</span>}
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {resumen.fuente === 'semana' && resumen.motivo && (
        <div className="mt-1 text-xs text-muted-foreground">Motivo del ajuste: {resumen.motivo}</div>
      )}
      {exceeds && (
        <div className="mt-1 text-xs text-red-600 dark:text-red-400">No se pueden agregar más horas esta semana. Para ampliar, solicítalo al área administrativa.</div>
      )}
    </div>
  );
};

// Día de la semana de hoy ('lunes'…'domingo')
const todayWeekDay = () => DAYS_OF_WEEK[(new Date().getDay() + 6) % 7];

const hourLabel = (h) => String(h % 24).padStart(2, '0');
const isNightHour = (h) => {
  const s = toMinutes(NIGHT_START) / 60, e = toMinutes(NIGHT_END) / 60;
  const hh = h % 24;
  return hh >= s || hh < e;
};

// Intensidad de una celda del mapa: más personas = color más fuerte
const heatStyle = (value, max, color) => {
  if (!value) return undefined;
  const pct = Math.round(15 + (75 * value) / Math.max(1, max));
  return { background: `color-mix(in srgb, ${color} ${pct}%, transparent)`, color: pct > 55 ? '#fff' : undefined };
};

// ─── Vista por horas de la planificación ────────────────────────────
// Para un día de la semana: cuántas personas entran y cuántas están en turno en cada hora,
// y la barra del turno de cada empleado (color del rol). Fuera de ShiftCalendar para que no
// se vuelva a montar en cada render del padre.
const HourlyPlanView = ({ assignments, day, onDayChange, weekDates }) => {
  const dayIdx = DAYS_OF_WEEK.indexOf(day);
  const prevDay = dayIdx > 0 ? DAYS_OF_WEEK[dayIdx - 1] : null;

  const peopleByDay = useMemo(
    () => DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: assignments.filter((a) => (a.horarios?.[d] || []).length > 0).length }), {}),
    [assignments],
  );

  const info = useMemo(() => {
    const rows = assignments
      .map((a) => {
        const segs = (a.horarios?.[day] || []).map((r) => {
          const [ini, fin] = rangeBounds(r);
          return { ini, fin, label: `${r.inicio}–${r.fin}` };
        }).filter((s) => s.fin > s.ini);
        // Turno nocturno del día anterior que termina en la madrugada de este día
        const carry = prevDay
          ? (a.horarios?.[prevDay] || []).map((r) => {
              const [ini, fin] = rangeBounds(r);
              return fin > 24 * 60 ? { ini: 0, fin: fin - 24 * 60, label: `${r.inicio}–${r.fin} (viene del ${DAYS_LABELS[prevDay].toLowerCase()})`, carry: true } : null;
            }).filter(Boolean)
          : [];
        const all = [...carry, ...segs];
        if (!all.length) return null;
        const d = dayMinutes(a.horarios?.[day] || [], a.almuerzo?.[day]);
        return {
          id: a._id,
          name: `${a.empleado?.nombre || ''} ${a.empleado?.apellido || ''}`.trim() || 'Sin nombre',
          role: a.shiftRole?.nombre || 'Sin rol',
          color: a.shiftRole?.color || '#64748b',
          lunch: Number(a.almuerzo?.[day]) || 0,
          segs: all,
          starts: segs.map((s) => s.ini),
          worked: d.worked,
          night: d.night,
        };
      })
      .filter(Boolean)
      .sort((x, y) => Math.min(...x.segs.map((s) => s.ini)) - Math.min(...y.segs.map((s) => s.ini)) || x.name.localeCompare(y.name));

    const mins = rows.flatMap((r) => r.segs.flatMap((s) => [s.ini, s.fin]));
    let h0 = mins.length ? Math.max(0, Math.floor(Math.min(...mins) / 60) - 1) : 6;
    let h1 = mins.length ? Math.ceil(Math.max(...mins) / 60) + 1 : 20;
    h1 = Math.min(48, Math.max(h1, h0 + 8));
    const hours = Array.from({ length: h1 - h0 }, (_, i) => h0 + i);

    const byHour = hours.map((h) => ({
      h,
      arrivals: rows.reduce((acc, r) => acc + r.starts.filter((m) => Math.floor(m / 60) === h).length, 0),
      onShift: rows.filter((r) => r.segs.some((s) => s.ini < (h + 1) * 60 && s.fin > h * 60)).length,
    }));
    const maxArr = Math.max(0, ...byHour.map((b) => b.arrivals));
    const maxOn = Math.max(0, ...byHour.map((b) => b.onShift));
    const peak = byHour.find((b) => b.arrivals === maxArr && maxArr > 0);
    const peakOn = byHour.filter((b) => b.onShift === maxOn && maxOn > 0);
    return {
      rows, hours, h0, h1, byHour, maxArr, maxOn, peak, peakOn,
      totalHours: round1(rows.reduce((acc, r) => acc + r.worked, 0) / 60),
      nightHours: round1(rows.reduce((acc, r) => acc + r.night, 0) / 60),
    };
  }, [assignments, day, prevDay]);

  const { rows, hours, h0, h1, byHour, maxArr, maxOn, peak, peakOn, totalHours, nightHours } = info;
  const cols = { gridTemplateColumns: `200px repeat(${hours.length}, minmax(30px, 1fr))` };
  const span = (h1 - h0) * 60;
  const pos = (m) => `${((m - h0 * 60) / span) * 100}%`;
  const width = (a, b) => `${((b - a) / span) * 100}%`;
  const date = weekDates?.[dayIdx];

  const heatRow = (label, key, max, color) => (
    <>
      <div className="flex items-center pr-2 text-xs text-muted-foreground">{label}</div>
      {byHour.map((b) => (
        <div
          key={b.h}
          className={`h-7 flex items-center justify-center rounded text-xs font-semibold tabular-nums ${b[key] ? '' : 'bg-muted/40'}`}
          style={heatStyle(b[key], max, color)}
          title={`${hourLabel(b.h)}:00–${hourLabel(b.h + 1)}:00 · ${b[key]} ${b[key] === 1 ? 'persona' : 'personas'}`}
        >
          {b[key] || ''}
        </div>
      ))}
    </>
  );

  return (
    <div className="space-y-3">
      {/* Selector de día */}
      <div className="flex flex-wrap gap-1.5">
        {DAYS_OF_WEEK.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => onDayChange(d)}
            className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${d === day ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          >
            <span className="font-semibold">{DAYS_LABELS[d].substring(0, 3)}</span>
            {weekDates?.[i] && <span className="ml-1">{format(weekDates[i], 'd')}</span>}
            <span className={`ml-1.5 ${d === day ? 'opacity-80' : 'text-muted-foreground'}`}>· {peopleByDay[d]}</span>
          </button>
        ))}
      </div>

      <div className="text-sm">
        <span className="font-semibold">
          {DAYS_LABELS[day]}{date ? ` ${format(date, "d 'de' MMMM", { locale: es })}` : ''}
        </span>
        <span className="text-muted-foreground">
          {rows.length
            ? ` · ${peopleByDay[day]} persona(s) · ${totalHours}h planeadas${nightHours > 0 ? ` (${nightHours}h nocturnas)` : ''}` +
              (peak ? ` · Pico de llegada: ${hourLabel(peak.h)}:00 (${peak.arrivals})` : '') +
              (peakOn.length ? ` · Máximo en turno: ${maxOn} de ${hourLabel(peakOn[0].h)}:00 a ${hourLabel(peakOn[peakOn.length - 1].h + 1)}:00` : '')
            : ' · Nadie tiene turno este día.'}
        </span>
      </div>

      {rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <div className="grid gap-0.5 min-w-max text-xs" style={cols}>
              <div />
              {hours.map((h) => (
                <div
                  key={h}
                  className={`text-center font-mono text-[10.5px] rounded-t ${isNightHour(h) ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40' : 'text-muted-foreground'}`}
                  title={isNightHour(h) ? 'Hora nocturna' : undefined}
                >
                  {hourLabel(h)}
                </div>
              ))}
              {heatRow('Llegadas', 'arrivals', maxArr, '#2563eb')}
              {heatRow('En turno', 'onShift', maxOn, '#059669')}
              <div className="h-px bg-border my-1.5" style={{ gridColumn: '1 / -1' }} />

              {rows.map((r) => (
                <div key={r.id} className="grid gap-0.5" style={{ gridColumn: '1 / -1', ...cols }}>
                  <div className="flex items-center gap-1.5 min-w-0 pr-2" title={`${r.name} · ${r.role}`}>
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                    <span className="truncate text-xs font-medium">{r.name}</span>
                  </div>
                  <div className="relative h-7 rounded overflow-hidden" style={{ gridColumn: '2 / -1' }}>
                    {/* Fondo: una franja por hora; las nocturnas resaltadas */}
                    <div className="absolute inset-0 flex">
                      {hours.map((h) => (
                        <div key={h} className={`flex-1 border-l border-border/60 ${isNightHour(h) ? 'bg-indigo-50/80 dark:bg-indigo-950/30' : 'bg-muted/30'}`} />
                      ))}
                    </div>
                    {r.segs.map((s) => (
                      <div
                        key={`${s.ini}-${s.fin}`}
                        className={`absolute top-1 bottom-1 rounded px-1.5 text-[10.5px] leading-5 whitespace-nowrap overflow-hidden border ${s.carry ? 'border-dashed' : ''}`}
                        style={{
                          left: pos(s.ini),
                          width: width(s.ini, s.fin),
                          background: `color-mix(in srgb, ${r.color} 28%, transparent)`,
                          borderColor: `color-mix(in srgb, ${r.color} 70%, transparent)`,
                        }}
                        title={`${r.name} · ${r.role}\nTurno ${s.label}${r.lunch && !s.carry ? `\nAlmuerzo: ${r.lunch} min` : ''}`}
                      >
                        {s.label.split(' (')[0]}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 rounded-sm bg-indigo-100 dark:bg-indigo-900/50" /> Horas nocturnas ({NIGHT_START}–{NIGHT_END})</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 rounded-sm border border-dashed border-muted-foreground" /> Turno que viene del día anterior</span>
            <span>El color de cada barra es el del rol. El número de las celdas es la cantidad de personas en esa hora.</span>
          </div>
        </>
      )}
    </div>
  );
};

const ROLE_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const ShiftCalendar = () => {
  const { idLavanderia } = useParams();
  const { isAdmin, user, ROLES } = useAuth();

  const { toast } = useToast();

  // Permisos
  const canManage = useMemo(() => (isAdmin() || user?.role === ROLES.SUPERVISOR), [isAdmin, user, ROLES]);

  // ─── Estado global ────────────────────────────────────────────────
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  // Tipos de horario
  const [scheduleTypes, setScheduleTypes] = useState([]);
  const [loadingScheduleTypes, setLoadingScheduleTypes] = useState(false);

  // Roles
  const [shiftRoles, setShiftRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Asignaciones semanales
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Resumen semanal: null = tabla; un día ('lunes'…) = vista por horas de ese día
  const [hourViewDay, setHourViewDay] = useState(null);

  // Modales
  const [scheduleTypeModal, setScheduleTypeModal] = useState({ open: false, editing: null });
  const [roleModal, setRoleModal] = useState({ open: false, editing: null });

  // Formulario de asignación
  const [selectedScheduleType, setSelectedScheduleType] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Semana ───────────────────────────────────────────────────────
  const weekStartsOn = 1;
  const weekInterval = eachDayOfInterval({
    start: startOfWeek(currentDate, { weekStartsOn }),
    end: endOfWeek(currentDate, { weekStartsOn }),
  });
  const weekDays = weekInterval.map(day => ({
    date: day,
    name: format(day, 'EEEE', { locale: es }),
    dayNumber: format(day, 'd'),
    key: format(day, 'yyyy-MM-dd'),
  }));
  const weekStartStr = format(weekInterval[0], 'yyyy-MM-dd');
  const weekEndStr = format(weekInterval[6], 'yyyy-MM-dd');

  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));

  // ─── Carga de datos ───────────────────────────────────────────────
  useEffect(() => {
    if (!idLavanderia) return;
    const load = async () => {
      setLoadingEmployees(true);
      try {
        const response = await fetchEmployees({ query: { "lavanderia.id": idLavanderia, estado: "Activo" }, limit: 200 });
        const formatted = (response.data || []).map(emp => ({
          id: emp._id,
          nombre: emp.nombre || '',
          apellido: emp.apellido || '',
          name: `${emp.nombre || ''} ${emp.apellido || ''}`.trim(),
          cedula: emp.cedula || '',
          rol: emp.rol || '',
          conductor: isDriverRol(emp.rol),
        }));
        setEmployees(formatted);
      } catch {
        toast({ title: 'Error', description: 'No se pudieron cargar los empleados.', variant: 'destructive' });
      } finally {
        setLoadingEmployees(false);
      }
    };
    load();
  }, [idLavanderia]);

  const loadScheduleTypes = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingScheduleTypes(true);
    try {
      const data = await getScheduleTypes(idLavanderia);
      setScheduleTypes(data || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los tipos de horario.', variant: 'destructive' });
    } finally {
      setLoadingScheduleTypes(false);
    }
  }, [idLavanderia]);

  const loadRoles = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingRoles(true);
    try {
      const data = await getShiftRoles(idLavanderia);
      setShiftRoles(data || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los roles.', variant: 'destructive' });
    } finally {
      setLoadingRoles(false);
    }
  }, [idLavanderia]);

  const loadAssignments = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingAssignments(true);
    try {
      const data = await getWeeklyAssignments(idLavanderia, weekStartStr);
      setAssignments(data || []);
    } catch {
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [idLavanderia, weekStartStr]);

  // Horas autorizadas del centro para la semana (se recarga cada vez que cambian las asignaciones)
  const [authorizedHours, setAuthorizedHours] = useState(null);
  const [loadingAuthorized, setLoadingAuthorized] = useState(false);
  const loadAuthorizedHours = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingAuthorized(true);
    try {
      setAuthorizedHours(await obtenerResumenSemana(idLavanderia, weekStartStr));
    } catch {
      setAuthorizedHours(null);
    } finally {
      setLoadingAuthorized(false);
    }
  }, [idLavanderia, weekStartStr]);
  useEffect(() => { loadAuthorizedHours(); }, [loadAuthorizedHours, assignments]);

  // Conductores: sus horas no cuentan en el límite
  const driverIds = useMemo(
    () => new Set([...(authorizedHours?.conductores || []), ...employees.filter((e) => e.conductor).map((e) => e.id)].map(String)),
    [authorizedHours, employees],
  );

  useEffect(() => { loadScheduleTypes(); }, [loadScheduleTypes]);
  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // ─── Empleados ya asignados esta semana ───────────────────────────
  const assignedEmployeeIds = useMemo(() => new Set(assignments.map(a => a.employeeId)), [assignments]);
  const availableEmployees = useMemo(() => employees.filter(e => !assignedEmployeeIds.has(e.id)), [employees, assignedEmployeeIds]);

  // ═══════════════════════════════════════════════════════════════════
  // TAB 1: TIPOS DE HORARIO
  // ═══════════════════════════════════════════════════════════════════
  const ScheduleTypesTab = () => {
    const emptyAlmuerzo = () => DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: 0 }), {});
    const [form, setForm] = useState({
      nombre: '',
      descripcion: '',
      horarios: DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: [] }), {}),
      almuerzo: emptyAlmuerzo(),
    });

    useEffect(() => {
      if (scheduleTypeModal.editing) {
        const e = scheduleTypeModal.editing;
        setForm({
          nombre: e.nombre || '',
          descripcion: e.descripcion || '',
          horarios: e.horarios || DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: [] }), {}),
          almuerzo: e.almuerzo || emptyAlmuerzo(),
        });
      } else {
        setForm({
          nombre: '',
          descripcion: '',
          horarios: DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: [] }), {}),
          almuerzo: emptyAlmuerzo(),
        });
      }
    }, [scheduleTypeModal.editing]);

    const addRange = (day) => {
      setForm(prev => ({
        ...prev,
        horarios: {
          ...prev.horarios,
          [day]: [...(prev.horarios[day] || []), { inicio: '08:00', fin: '17:00' }],
        },
      }));
    };

    const removeRange = (day, idx) => {
      setForm(prev => ({
        ...prev,
        horarios: {
          ...prev.horarios,
          [day]: prev.horarios[day].filter((_, i) => i !== idx),
        },
      }));
    };

    const updateRange = (day, idx, field, value) => {
      setForm(prev => ({
        ...prev,
        horarios: {
          ...prev.horarios,
          [day]: prev.horarios[day].map((r, i) => i === idx ? { ...r, [field]: value } : r),
        },
      }));
    };

    const handleSave = async () => {
      if (!form.nombre.trim()) {
        toast({ title: 'Error', description: 'El nombre es requerido.', variant: 'destructive' });
        return;
      }
      try {
        if (scheduleTypeModal.editing?._id) {
          await updateScheduleType(scheduleTypeModal.editing._id, form);
          toast({ title: 'Actualizado', description: 'Tipo de horario actualizado.' });
        } else {
          await createScheduleType({ ...form, laundryCenterId: idLavanderia });
          toast({ title: 'Creado', description: 'Tipo de horario creado.' });
        }
        setScheduleTypeModal({ open: false, editing: null });
        loadScheduleTypes();
      } catch (err) {
        toast({ title: 'Error', description: err.message || 'No se pudo guardar.', variant: 'destructive' });
      }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('¿Eliminar este tipo de horario?')) return;
      try {
        await deleteScheduleType(id);
        toast({ title: 'Eliminado' });
        loadScheduleTypes();
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Tipos de Horario</h3>
          <Button size="sm" onClick={() => setScheduleTypeModal({ open: true, editing: null })}>
            <Plus className="h-4 w-4 mr-1" /> Nuevo Horario
          </Button>
        </div>

        {loadingScheduleTypes && <p className="text-sm text-muted-foreground">Cargando...</p>}

        {!loadingScheduleTypes && scheduleTypes.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No hay tipos de horario creados. Crea uno para comenzar.</p>
        )}

        <div className="grid gap-3">
          {scheduleTypes.map(st => (
            <Card key={st._id} className="border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-base">{st.nombre}</h4>
                    {st.descripcion && <p className="text-sm text-muted-foreground">{st.descripcion}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setScheduleTypeModal({ open: true, editing: st })}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(st._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-2 text-xs">
                  {DAYS_OF_WEEK.map(day => (
                    <div key={day} className="text-center">
                      <div className="font-medium mb-1">{DAYS_LABELS[day].substring(0, 3)}</div>
                      {(st.horarios?.[day] || []).length === 0 ? (
                        <span className="text-muted-foreground">Libre</span>
                      ) : (
                        <>
                          {(st.horarios[day] || []).map((r, i) => (
                            <div key={i} className="bg-blue-100 dark:bg-blue-900/30 rounded px-1 py-0.5 mb-0.5">
                              {r.inicio} - {r.fin}
                            </div>
                          ))}
                          {(st.almuerzo?.[day] || 0) > 0 && (
                            <div className="bg-amber-100 dark:bg-amber-900/30 rounded px-1 py-0.5 text-[10px]">
                              Almuerzo: {st.almuerzo[day]}min
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal crear/editar tipo de horario */}
        <Dialog open={scheduleTypeModal.open} onOpenChange={(v) => !v && setScheduleTypeModal({ open: false, editing: null })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{scheduleTypeModal.editing?._id ? 'Editar' : 'Nuevo'} Tipo de Horario</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Tipo 1 - Mañana" />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} placeholder="Opcional" />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-base">Horarios por día</Label>
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="border rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{DAYS_LABELS[day]}</span>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addRange(day)}>
                        <Plus className="h-3 w-3 mr-1" /> Rango
                      </Button>
                    </div>
                    {(form.horarios[day] || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">Sin horario (día libre)</p>
                    )}
                    {(form.horarios[day] || []).map((range, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-1">
                        <Input
                          type="time"
                          className="w-32 h-8 text-sm"
                          value={range.inicio}
                          onChange={e => updateRange(day, idx, 'inicio', e.target.value)}
                        />
                        <span className="text-sm">a</span>
                        <Input
                          type="time"
                          className="w-32 h-8 text-sm"
                          value={range.fin}
                          onChange={e => updateRange(day, idx, 'fin', e.target.value)}
                        />
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeRange(day, idx)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {(form.horarios[day] || []).length > 0 && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Almuerzo (min):</span>
                        <Input
                          type="number"
                          min={0}
                          max={120}
                          step={15}
                          className="w-20 h-7 text-xs"
                          value={form.almuerzo[day] || 0}
                          onChange={e => {
                            const val = Math.max(0, Math.min(120, parseInt(e.target.value) || 0));
                            setForm(prev => ({ ...prev, almuerzo: { ...prev.almuerzo, [day]: val } }));
                          }}
                        />
                        <span className="text-[10px] text-muted-foreground">No cuenta como hora trabajada</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setScheduleTypeModal({ open: false, editing: null })}>Cancelar</Button>
              <Button onClick={handleSave}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // TAB 2: ROLES
  // ═══════════════════════════════════════════════════════════════════
  const RolesTab = () => {
    const [form, setForm] = useState({ nombre: '', descripcion: '', color: ROLE_COLORS[0] });

    useEffect(() => {
      if (roleModal.editing) {
        setForm({
          nombre: roleModal.editing.nombre || '',
          descripcion: roleModal.editing.descripcion || '',
          color: roleModal.editing.color || ROLE_COLORS[0],
        });
      } else {
        setForm({ nombre: '', descripcion: '', color: ROLE_COLORS[shiftRoles.length % ROLE_COLORS.length] });
      }
    }, [roleModal.editing, shiftRoles.length]);

    const handleSave = async () => {
      if (!form.nombre.trim()) {
        toast({ title: 'Error', description: 'El nombre es requerido.', variant: 'destructive' });
        return;
      }
      try {
        if (roleModal.editing?._id) {
          await updateShiftRole(roleModal.editing._id, form);
          toast({ title: 'Actualizado', description: 'Rol actualizado.' });
        } else {
          await createShiftRole({ ...form, laundryCenterId: idLavanderia });
          toast({ title: 'Creado', description: 'Rol creado.' });
        }
        setRoleModal({ open: false, editing: null });
        loadRoles();
      } catch (err) {
        toast({ title: 'Error', description: err.message || 'No se pudo guardar.', variant: 'destructive' });
      }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('¿Eliminar este rol?')) return;
      try {
        await deleteShiftRole(id);
        toast({ title: 'Eliminado' });
        loadRoles();
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Roles de Turno</h3>
          <Button size="sm" onClick={() => setRoleModal({ open: true, editing: null })}>
            <Plus className="h-4 w-4 mr-1" /> Nuevo Rol
          </Button>
        </div>

        {loadingRoles && <p className="text-sm text-muted-foreground">Cargando...</p>}

        {!loadingRoles && shiftRoles.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No hay roles creados. Crea uno para comenzar.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shiftRoles.map(role => (
            <Card key={role._id} className="border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: role.color || '#888' }} />
                    <div>
                      <h4 className="font-semibold">{role.nombre}</h4>
                      {role.descripcion && <p className="text-xs text-muted-foreground">{role.descripcion}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRoleModal({ open: true, editing: role })}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(role._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal crear/editar rol */}
        <Dialog open={roleModal.open} onOpenChange={(v) => !v && setRoleModal({ open: false, editing: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{roleModal.editing?._id ? 'Editar' : 'Nuevo'} Rol</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Supervisor" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} placeholder="Opcional" />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {ROLE_COLORS.map(c => (
                    <button
                      key={c}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setForm(p => ({ ...p, color: c }))}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoleModal({ open: false, editing: null })}>Cancelar</Button>
              <Button onClick={handleSave}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // TAB 3: ASIGNACIÓN
  // ═══════════════════════════════════════════════════════════════════
  const AssignmentTab = () => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredEmployees = useMemo(() => {
      if (!searchTerm.trim()) return availableEmployees;
      const q = searchTerm.toLowerCase();
      return availableEmployees.filter(e =>
        e.name.toLowerCase().includes(q) || (e.cedula || '').includes(q)
      );
    }, [availableEmployees, searchTerm]);

    const toggleEmployee = (empId) => {
      setSelectedEmployeeIds(prev =>
        prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
      );
    };

    const selectAll = () => {
      setSelectedEmployeeIds(filteredEmployees.map(e => e.id));
    };

    const deselectAll = () => {
      setSelectedEmployeeIds([]);
    };

    const selectedScheduleTypeObj = scheduleTypes.find(s => s._id === selectedScheduleType);
    const selectedRoleObj = shiftRoles.find(r => r._id === selectedRole);

    // Horas que suma esta asignación frente al límite autorizado (los conductores no cuentan)
    const typeHours = selectedScheduleTypeObj ? weekHoursOf(selectedScheduleTypeObj.horarios, selectedScheduleTypeObj.almuerzo) : 0;
    const countedSelected = selectedEmployeeIds.filter((id) => !driverIds.has(String(id))).length;
    const addedHours = round1(typeHours * countedSelected);
    const limit = authorizedHours?.limite ?? null;
    const hoursAfter = round1((authorizedHours?.planificadas || 0) + addedHours);
    const exceedsLimit = limit != null && addedHours > 0 && hoursAfter > limit;

    const handleAssign = async () => {
      if (!selectedScheduleType) {
        toast({ title: 'Error', description: 'Selecciona un tipo de horario.', variant: 'destructive' });
        return;
      }
      if (!selectedRole) {
        toast({ title: 'Error', description: 'Selecciona un rol.', variant: 'destructive' });
        return;
      }
      if (selectedEmployeeIds.length === 0) {
        toast({ title: 'Error', description: 'Selecciona al menos un empleado.', variant: 'destructive' });
        return;
      }

      setIsSaving(true);
      try {
        const empleados = selectedEmployeeIds.map(id => {
          const emp = employees.find(e => e.id === id);
          return emp ? { id: emp.id, nombre: emp.nombre, apellido: emp.apellido, cedula: emp.cedula } : { id };
        });

        await createAssignments({
          laundryCenterId: idLavanderia,
          weekStart: weekStartStr,
          weekEnd: weekEndStr,
          scheduleTypeId: selectedScheduleType,
          shiftRoleId: selectedRole,
          employeeIds: selectedEmployeeIds,
          empleados,
        });

        toast({ title: 'Asignados', description: `${selectedEmployeeIds.length} empleado(s) asignados correctamente.` });
        setSelectedEmployeeIds([]);
        loadAssignments();
      } catch (err) {
        toast({ title: 'Error', description: err.message || 'No se pudieron crear las asignaciones.', variant: 'destructive' });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Asignar Empleados</h3>

        {scheduleTypes.length === 0 || shiftRoles.length === 0 ? (
          <div className="border rounded-md p-6 text-center text-muted-foreground">
            <p>Debes crear al menos un <strong>Tipo de Horario</strong> y un <strong>Rol</strong> antes de asignar empleados.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Columna izquierda: selección de horario y rol */}
            <div className="space-y-4">
              <div>
                <Label>Tipo de Horario</Label>
                <Select value={selectedScheduleType} onValueChange={setSelectedScheduleType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar horario" />
                  </SelectTrigger>
                  <SelectContent>
                    {scheduleTypes.map(st => (
                      <SelectItem key={st._id} value={st._id}>{st.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Rol</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {shiftRoles.map(r => (
                      <SelectItem key={r._id} value={r._id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color || '#888' }} />
                          {r.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview del horario seleccionado */}
              {selectedScheduleTypeObj && (
                <Card className="border">
                  <CardContent className="p-3">
                    <p className="text-sm font-medium mb-2">Vista previa: {selectedScheduleTypeObj.nombre}</p>
                    <div className="space-y-1 text-xs">
                      {DAYS_OF_WEEK.map(day => {
                        const ranges = selectedScheduleTypeObj.horarios?.[day] || [];
                        return (
                          <div key={day} className="flex items-center gap-2">
                            <span className="w-12 font-medium">{DAYS_LABELS[day].substring(0, 3)}</span>
                            {ranges.length === 0 ? (
                              <span className="text-muted-foreground">Libre</span>
                            ) : (
                              <>
                                {ranges.map((r, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{r.inicio} - {r.fin}</Badge>
                                ))}
                                {(selectedScheduleTypeObj.almuerzo?.[day] || 0) > 0 && (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                    Alm: {selectedScheduleTypeObj.almuerzo[day]}min
                                  </Badge>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedScheduleTypeObj && selectedEmployeeIds.length > 0 && (
                <div className={`rounded-md border p-2.5 text-xs ${exceedsLimit ? 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-300' : 'bg-muted/30'}`}>
                  <div>
                    Esta asignación suma <strong>{addedHours} h</strong> ({countedSelected} empleado(s) × {typeHours} h)
                    {selectedEmployeeIds.length > countedSelected && ` · ${selectedEmployeeIds.length - countedSelected} conductor(es) no cuentan`}
                  </div>
                  {limit != null ? (
                    <div className="mt-0.5">
                      La semana quedaría en <strong>{hoursAfter} h</strong> de {limit} h autorizadas.
                    </div>
                  ) : (
                    <div className="mt-0.5 text-muted-foreground">El centro no tiene límite de horas configurado.</div>
                  )}
                  {exceedsLimit && (
                    <div className="mt-1 font-semibold">Supera las horas autorizadas. Solicita la ampliación al área administrativa.</div>
                  )}
                </div>
              )}

              <Button className="w-full" disabled={isSaving || exceedsLimit} onClick={handleAssign}>
                {isSaving ? 'Asignando...' : `Asignar ${selectedEmployeeIds.length} empleado(s)`}
              </Button>
            </div>

            {/* Columna central: lista de empleados disponibles */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Input
                  placeholder="Buscar empleado..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Button variant="outline" size="sm" onClick={selectAll}>Todos</Button>
                <Button variant="outline" size="sm" onClick={deselectAll}>Ninguno</Button>
              </div>

              {loadingEmployees ? (
                <p className="text-sm text-muted-foreground">Cargando empleados...</p>
              ) : filteredEmployees.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  {availableEmployees.length === 0
                    ? 'Todos los empleados ya están asignados esta semana.'
                    : 'No se encontraron empleados con ese criterio.'}
                </p>
              ) : (
                <div className="border rounded-md max-h-[400px] overflow-y-auto">
                  {filteredEmployees.map(emp => (
                    <div
                      key={emp.id}
                      className={`flex items-center gap-3 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${selectedEmployeeIds.includes(emp.id) ? 'bg-primary/10' : ''}`}
                      onClick={() => toggleEmployee(emp.id)}
                    >
                      <Checkbox checked={selectedEmployeeIds.includes(emp.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{emp.name}</p>
                        <p className="text-xs text-muted-foreground">{emp.cedula}</p>
                      </div>
                      {emp.conductor && (
                        <Badge variant="outline" className="text-[10px] shrink-0" title="Las horas de los conductores no cuentan en el límite autorizado">
                          Conductor · no cuenta en el límite
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {selectedEmployeeIds.length > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  {selectedEmployeeIds.length} empleado(s) seleccionado(s)
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // TAB 4: RESUMEN SEMANAL (con edición inline y alertas de horas)
  // ═══════════════════════════════════════════════════════════════════
  const WeeklySummaryTab = () => {
    const [editModal, setEditModal] = useState({ open: false, assignment: null });
    const [editForm, setEditForm] = useState(DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: [] }), {}));
    const [editAlmuerzo, setEditAlmuerzo] = useState(DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: 0 }), {}));
    const [savingEdit, setSavingEdit] = useState(false);

    const openEditModal = (assignment) => {
      const horarios = {};
      const almuerzo = {};
      for (const day of DAYS_OF_WEEK) {
        horarios[day] = (assignment.horarios?.[day] || []).map(r => ({ inicio: r.inicio || '08:00', fin: r.fin || '17:00' }));
        almuerzo[day] = assignment.almuerzo?.[day] || 0;
      }
      setEditForm(horarios);
      setEditAlmuerzo(almuerzo);
      setEditModal({ open: true, assignment });
    };

    const addEditRange = (day) => {
      setEditForm(prev => ({ ...prev, [day]: [...(prev[day] || []), { inicio: '08:00', fin: '17:00' }] }));
    };

    const removeEditRange = (day, idx) => {
      setEditForm(prev => {
        const nextRanges = prev[day].filter((_, i) => i !== idx);
        // Si se eliminan todos los rangos, resetear almuerzo a 0
        if (nextRanges.length === 0) {
          setEditAlmuerzo(prevAlmuerzo => ({ ...prevAlmuerzo, [day]: 0 }));
        }
        return { ...prev, [day]: nextRanges };
      });
    };

    const updateEditRange = (day, idx, field, value) => {
      setEditForm(prev => ({ ...prev, [day]: prev[day].map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
    };

    const handleSaveEdit = async () => {
      if (!editModal.assignment?._id) return;
      setSavingEdit(true);
      try {
        await updateAssignment(editModal.assignment._id, { horarios: editForm, almuerzo: editAlmuerzo });
        toast({ title: 'Actualizado', description: 'Horarios del empleado actualizados.' });
        setEditModal({ open: false, assignment: null });
        loadAssignments();
      } catch (err) {
        toast({ title: 'Error', description: err.message || 'No se pudo actualizar.', variant: 'destructive' });
      } finally {
        setSavingEdit(false);
      }
    };

    const handleDeleteAssignment = async (id) => {
      if (!window.confirm('¿Eliminar esta asignación?')) return;
      try {
        await deleteAssignment(id);
        toast({ title: 'Eliminado' });
        loadAssignments();
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    };

    const groupedByRole = useMemo(() => {
      const groups = {};
      for (const a of assignments) {
        const roleKey = a.shiftRole?.nombre || 'Sin rol';
        if (!groups[roleKey]) groups[roleKey] = { role: a.shiftRole, items: [] };
        groups[roleKey].items.push(a);
      }
      return Object.values(groups);
    }, [assignments]);

    // Horas de la semana: total (sin almuerzo) y cuántas caen en horario nocturno
    const calcWeekHours = (horarios, almuerzo) => {
      if (!horarios) return { total: 0, nocturnas: 0 };
      let worked = 0, night = 0;
      for (const day of DAYS_OF_WEEK) {
        const d = dayMinutes(horarios[day] || [], almuerzo?.[day]);
        worked += d.worked;
        night += d.night;
      }
      return { total: round1(worked / 60), nocturnas: round1(night / 60) };
    };

    const calcBreakdown = (horarios, almuerzo) => {
      const { total, nocturnas } = calcWeekHours(horarios, almuerzo);
      const ordinarias = Math.min(total, BASE_WEEKLY_HOURS);
      const extras = Math.max(0, Math.round((total - BASE_WEEKLY_HOURS) * 10) / 10);
      const alerts = [];
      if (extras > 0 && extras <= MAX_OVERTIME_HOURS) {
        alerts.push({ type: 'warning', msg: `${extras}h extras` });
      }
      if (extras > MAX_OVERTIME_HOURS) {
        alerts.push({ type: 'danger', msg: `Excede máx. extras (${extras}h / ${MAX_OVERTIME_HOURS}h permitidas)` });
      }
      if (total > MAX_WEEKLY_HOURS) {
        alerts.push({ type: 'danger', msg: `Excede ${MAX_WEEKLY_HOURS}h semanales permitidas` });
      }
      if (total === 0) {
        alerts.push({ type: 'info', msg: 'Sin horas asignadas' });
      }
      return { total, ordinarias: Math.round(ordinarias * 10) / 10, extras, nocturnas, alerts };
    };

    const editingName = editModal.assignment
      ? `${editModal.assignment.empleado?.nombre || ''} ${editModal.assignment.empleado?.apellido || ''}`.trim()
      : '';

    const editFormBreakdown = calcBreakdown(editForm, editAlmuerzo);

    // Límite autorizado: el cambio reemplaza las horas actuales del empleado en la semana
    const editIsDriver = editModal.assignment ? driverIds.has(String(editModal.assignment.employeeId)) : false;
    const editOldHours = editModal.assignment ? weekHoursOf(editModal.assignment.horarios, editModal.assignment.almuerzo) : 0;
    const editNewHours = weekHoursOf(editForm, editAlmuerzo);
    const editLimit = authorizedHours?.limite ?? null;
    const editHoursAfter = round1((authorizedHours?.planificadas || 0) - (editIsDriver ? 0 : editOldHours) + (editIsDriver ? 0 : editNewHours));
    const editExceedsLimit = !editIsDriver && editLimit != null && editNewHours > editOldHours && editHoursAfter > editLimit;

    const globalTotals = useMemo(() => {
      let totalOrd = 0, totalExt = 0, totalNoc = 0, alertCount = 0;
      for (const a of assignments) {
        const b = calcBreakdown(a.horarios, a.almuerzo);
        totalOrd += b.ordinarias;
        totalExt += b.extras;
        totalNoc += b.nocturnas;
        if (b.alerts.some(al => al.type === 'danger' || al.type === 'warning')) alertCount++;
      }
      return {
        ordinarias: Math.round(totalOrd * 10) / 10,
        extras: Math.round(totalExt * 10) / 10,
        nocturnas: Math.round(totalNoc * 10) / 10,
        total: Math.round((totalOrd + totalExt) * 10) / 10,
        alertCount,
      };
    }, [assignments]);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold">Resumen Semanal</h3>
            <div className="inline-flex rounded-md border p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setHourViewDay(null)}
                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 ${!hourViewDay ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                <Table2 className="h-3.5 w-3.5" /> Tabla
              </button>
              <button
                type="button"
                onClick={() => setHourViewDay((d) => d || todayWeekDay())}
                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 ${hourViewDay ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                <Clock className="h-3.5 w-3.5" /> Por horas
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Base: {BASE_WEEKLY_HOURS}h | Máx. extras: {MAX_OVERTIME_HOURS}h | Nocturnas: {NIGHT_START}–{NIGHT_END}</span>
            <span className="text-sm text-muted-foreground">{assignments.length} empleado(s)</span>
          </div>
        </div>

        {loadingAssignments && <p className="text-sm text-muted-foreground">Cargando...</p>}

        {!loadingAssignments && assignments.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No hay asignaciones para esta semana.</p>
        )}

        {hourViewDay && !loadingAssignments && assignments.length > 0 && (
          <HourlyPlanView assignments={assignments} day={hourViewDay} onDayChange={setHourViewDay} weekDates={weekInterval} />
        )}

        {!hourViewDay && groupedByRole.map((group, gIdx) => (
          <Card key={gIdx} className="border">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.role?.color || '#888' }} />
                <CardTitle className="text-base">{group.role?.nombre || 'Sin rol'}</CardTitle>
                <Badge variant="secondary">{group.items.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="p-2 text-left font-medium">Empleado</th>
                      <th className="p-2 text-left font-medium">Horario</th>
                      {DAYS_OF_WEEK.map(day => (
                        <th key={day} className="p-2 text-center font-medium hidden md:table-cell">
                          <div className="inline-flex items-center gap-1">
                            {DAYS_LABELS[day].substring(0, 3)}
                            <button
                              type="button"
                              onClick={() => setHourViewDay(day)}
                              className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                              title={`Ver el ${DAYS_LABELS[day].toLowerCase()} por horas`}
                              aria-label={`Ver el ${DAYS_LABELS[day].toLowerCase()} por horas`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </th>
                      ))}
                      <th className="p-2 text-center font-medium">Ordinarias</th>
                      <th className="p-2 text-center font-medium">Extras</th>
                      <th className="p-2 text-center font-medium" title={`Horas entre ${NIGHT_START} y ${NIGHT_END}, incluidas en ordinarias/extras`}>Nocturnas</th>
                      <th className="p-2 text-center font-medium">Total</th>
                      <th className="p-2 text-center font-medium">Alertas</th>
                      <th className="p-2 text-center font-medium w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {group.items.map(a => {
                      const bd = calcBreakdown(a.horarios, a.almuerzo);
                      const hasWarning = bd.alerts.some(al => al.type === 'warning');
                      const hasDanger = bd.alerts.some(al => al.type === 'danger');
                      const rowClass = hasDanger
                        ? 'bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30'
                        : hasWarning
                        ? 'bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/30'
                        : 'hover:bg-muted/30';
                      return (
                        <tr key={a._id} className={rowClass}>
                          <td className="p-2">
                            <div className="font-medium">{a.empleado?.nombre || ''} {a.empleado?.apellido || ''}</div>
                            <div className="text-xs text-muted-foreground">{a.empleado?.cedula || ''}</div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">{a.scheduleType?.nombre || '-'}</Badge>
                          </td>
                          {DAYS_OF_WEEK.map(day => (
                            <td key={day} className="p-2 text-center hidden md:table-cell">
                              {a.horarios?.[day]?.length === 0 ? (
                                <span className="text-muted-foreground text-xs">-</span>
                              ) : (
                                <>
                                  {a.horarios[day].map((r, i) => (
                                    <div key={i} className="text-xs whitespace-nowrap">{r.inicio}-{r.fin}</div>
                                  ))}
                                  {(a.almuerzo?.[day] || 0) > 0 && (
                                    <div className="text-[10px] text-amber-600 dark:text-amber-400">Alm:{a.almuerzo[day]}m</div>
                                  )}
                                </>
                              )}
                            </td>
                          ))}
                          <td className="p-2 text-center font-semibold text-green-700 dark:text-green-400">{bd.ordinarias}h</td>
                          <td className="p-2 text-center font-semibold">
                            {bd.extras > 0 ? (
                              <span className={bd.extras > MAX_OVERTIME_HOURS ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>
                                {bd.extras}h
                              </span>
                            ) : (
                              <span className="text-muted-foreground">0h</span>
                            )}
                          </td>
                          <td className="p-2 text-center font-semibold">
                            {bd.nocturnas > 0 ? (
                              <span className="text-indigo-700 dark:text-indigo-400">{bd.nocturnas}h</span>
                            ) : (
                              <span className="text-muted-foreground">0h</span>
                            )}
                          </td>
                          <td className="p-2 text-center font-bold">{bd.total}h</td>
                          <td className="p-2 text-center">
                            {bd.alerts.length === 0 ? (
                              <span className="text-green-600 text-xs">✓ OK</span>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                {bd.alerts.map((al, i) => (
                                  <span key={i} className={`inline-flex items-center gap-1 text-[10px] leading-tight ${al.type === 'danger' ? 'text-red-600 dark:text-red-400 font-semibold' : al.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                                    <AlertTriangle className="h-3 w-3 shrink-0" />
                                    {al.msg}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {(user?.role === 'admin' || user?.role === 'supervisor') && (
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditModal(a)} title="Editar horarios">
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteAssignment(a._id)} title="Eliminar">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Totales */}
        {assignments.length > 0 && (
          <Card className="border bg-muted/30">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{assignments.length}</p>
                  <p className="text-xs text-muted-foreground">Empleados</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{new Set(assignments.map(a => a.shiftRoleId)).size}</p>
                  <p className="text-xs text-muted-foreground">Roles</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">{globalTotals.ordinarias}h</p>
                  <p className="text-xs text-muted-foreground">Horas Ordinarias</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${globalTotals.extras > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{globalTotals.extras}h</p>
                  <p className="text-xs text-muted-foreground">Horas Extras</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${globalTotals.nocturnas > 0 ? 'text-indigo-700 dark:text-indigo-400' : ''}`}>{globalTotals.nocturnas}h</p>
                  <p className="text-xs text-muted-foreground">Horas Nocturnas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{globalTotals.total}h</p>
                  <p className="text-xs text-muted-foreground">Horas Totales</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${authorizedHours?.excede ? 'text-red-600 dark:text-red-400' : ''}`}>
                    {authorizedHours?.limite != null ? `${authorizedHours.limite}h` : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {authorizedHours?.limite != null
                      ? `Autorizadas · ${authorizedHours.excede ? `excede ${round1(authorizedHours.planificadas - authorizedHours.limite)}h` : `${authorizedHours.disponibles}h disp.`}`
                      : 'Sin límite autorizado'}
                  </p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${globalTotals.alertCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {globalTotals.alertCount > 0 ? globalTotals.alertCount : '✓'}
                  </p>
                  <p className="text-xs text-muted-foreground">{globalTotals.alertCount > 0 ? 'Con Alertas' : 'Sin Alertas'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modal editar horarios de un empleado */}
        <Dialog open={editModal.open} onOpenChange={(v) => !v && setEditModal({ open: false, assignment: null })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Horarios — {editingName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Modifica los rangos horarios de cada día para este empleado. Los cambios solo aplican a esta asignación semanal.
              </p>
              {DAYS_OF_WEEK.map(day => (
                <div key={day} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{DAYS_LABELS[day]}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {(() => {
                          // Horas del rango (sin descontar almuerzo), con las nocturnas aparte
                          const d = dayMinutes(editForm[day] || [], 0);
                          if (d.worked <= 0) return '';
                          return `${round1(d.worked / 60)}h${d.night > 0 ? ` · ${round1(d.night / 60)}h noct.` : ''}`;
                        })()}
                      </span>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addEditRange(day)}>
                        <Plus className="h-3 w-3 mr-1" /> Rango
                      </Button>
                    </div>
                  </div>
                  {(editForm[day] || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin horario (día libre)</p>
                  )}
                  {(editForm[day] || []).map((range, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-1">
                      <Input
                        type="time"
                        className="w-32 h-8 text-sm"
                        value={range.inicio}
                        onChange={e => updateEditRange(day, idx, 'inicio', e.target.value)}
                      />
                      <span className="text-sm">a</span>
                      <Input
                        type="time"
                        className="w-32 h-8 text-sm"
                        value={range.fin}
                        onChange={e => updateEditRange(day, idx, 'fin', e.target.value)}
                      />
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeEditRange(day, idx)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {(editForm[day] || []).length > 0 && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Almuerzo (min):</span>
                      <Input
                        type="number"
                        min={0}
                        max={120}
                        step={15}
                        className="w-20 h-7 text-xs"
                        value={editAlmuerzo[day] || 0}
                        onChange={e => {
                          const val = Math.max(0, Math.min(120, parseInt(e.target.value) || 0));
                          setEditAlmuerzo(prev => ({ ...prev, [day]: val }));
                        }}
                      />
                      <span className="text-[10px] text-muted-foreground">No cuenta como hora trabajada</span>
                    </div>
                  )}
                </div>
              ))}
              {/* Resumen en el modal de edición */}
              <div className="border rounded-md p-3 bg-muted/30">
                <div className="flex items-center justify-between text-sm">
                  <span>Total semana: <strong>{editFormBreakdown.total}h</strong></span>
                  <span className="text-green-700 dark:text-green-400">Ordinarias: {editFormBreakdown.ordinarias}h</span>
                  <span className={editFormBreakdown.extras > 0 ? (editFormBreakdown.extras > MAX_OVERTIME_HOURS ? 'text-red-600 font-semibold' : 'text-amber-600') : 'text-muted-foreground'}>
                    Extras: {editFormBreakdown.extras}h
                  </span>
                  <span className={editFormBreakdown.nocturnas > 0 ? 'text-indigo-700 dark:text-indigo-400' : 'text-muted-foreground'}>
                    Nocturnas: {editFormBreakdown.nocturnas}h
                  </span>
                </div>
                {editFormBreakdown.alerts.filter(a => a.type !== 'info').map((al, i) => (
                  <div key={i} className={`flex items-center gap-1 mt-1 text-xs ${al.type === 'danger' ? 'text-red-600 font-semibold' : 'text-amber-600'}`}>
                    <AlertTriangle className="h-3 w-3" />
                    {al.msg}
                  </div>
                ))}
                <div className={`mt-2 pt-2 border-t text-xs ${editExceedsLimit ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                  {editIsDriver ? (
                    'Conductor: sus horas no cuentan en el límite autorizado del centro.'
                  ) : editLimit != null ? (
                    <>
                      Horas autorizadas del centro: la semana quedaría en <strong>{editHoursAfter} h</strong> de {editLimit} h
                      {editNewHours !== editOldHours && ` (${editNewHours > editOldHours ? '+' : ''}${round1(editNewHours - editOldHours)} h)`}.
                      {editExceedsLimit && <span className="block font-semibold mt-0.5">Supera las horas autorizadas. Solicita la ampliación al área administrativa.</span>}
                    </>
                  ) : (
                    'El centro no tiene límite de horas configurado.'
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, assignment: null })}>Cancelar</Button>
              <Button disabled={savingEdit || editExceedsLimit} onClick={handleSaveEdit}>
                {savingEdit ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════
  return (
    <>
    {(user?.role === 'admin' || user?.role === 'supervisor') && (
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-xl">Programación de Turnos</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="w-52 text-center font-semibold text-muted-foreground text-sm">
                {format(weekInterval[0], 'd MMM', { locale: es })} - {format(weekInterval[6], 'd MMM, yyyy', { locale: es })}
              </span>
              <Button variant="outline" size="icon" onClick={handleNextWeek}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <AuthorizedHoursBanner resumen={authorizedHours} loading={loadingAuthorized} />
          </div>
          <Tabs defaultValue="assign" className="w-full">
            {/* Navegación tipo tarjetas */}
            <TabsList className="flex h-auto w-full bg-transparent p-0 gap-1 ">
              <TabsTrigger
                value="schedules"
                className="flex-1 flex flex-col items-center gap-1 rounded-lg border-2 border-muted bg-card px-3 py-1 shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:shadow-md"
              >
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-blue-100 dark:bg-blue-900/40 p-1.5">
                    <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="font-semibold text-sm">Horarios</span>
                  {scheduleTypes.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{scheduleTypes.length}</Badge>
                  )}
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="roles"
                className="flex-1 flex flex-col items-center gap-1 rounded-lg border-2 border-muted bg-card px-3 py-1 shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:shadow-md"
              >
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-purple-100 dark:bg-purple-900/40 p-1.5">
                    <Shield className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="font-semibold text-sm">Roles</span>
                  {shiftRoles.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{shiftRoles.length}</Badge>
                  )}
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="assign"
                className="flex-1 flex flex-col items-center gap-1 rounded-lg border-2 border-muted bg-card px-3 py-1 shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:shadow-md"
              >
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-green-100 dark:bg-green-900/40 p-1.5">
                    <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="font-semibold text-sm">Asignar</span>
                  {availableEmployees.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{availableEmployees.length} disp.</Badge>
                  )}
                </div>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="schedules" className="mt-4">
              <ScheduleTypesTab />
            </TabsContent>

            <TabsContent value="roles" className="mt-4">
              <RolesTab />
            </TabsContent>

            <TabsContent value="assign" className="mt-4">
              {canManage ? <AssignmentTab /> : (
                <p className="text-sm text-muted-foreground py-4">No tienes permisos para asignar turnos.</p>
              )}
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>
    )}

    {/* Resumen semanal siempre visible */}
    <div className="mt-4">
      <Card>
        <CardContent className="pt-6">
          <WeeklySummaryTab />
        </CardContent>
      </Card>
    </div>
  </>
  );
};

export default ShiftCalendar;

